import { useState, useCallback } from 'react'
import customToastWithAlert from '../common/components/CustomToastWithAlert'

/**
 * * Custom hook for working with RTK Query mutations
 *  *
 *  * @param {Function} mutationHook - RTK Query mutation (for example, useAddTrainingTopicMutation)
 *  * @param {Object} options - Configuration parameters
 *  * @param {Function} options.onSuccess - Callback function on success
 *  * @param {Function} options.onError - Callback function on error
 *  * @param {string} options.successMessage - Success message
 *  * @param {string} options.errorMessage - Custom error message
 *  * @param {boolean} options.showSuccessToast - Whether to show success notification
 *  * @param {boolean} options.showErrorToast - Whether to show error notification
 * @returns {Object} { submit, data, isLoading, isError, error }
 */

export const useMutation = (mutationHook, options = {}) => {
  const {
    onSuccess,
    onError,
    successMessage = 'Operation successfully completed',
    errorMessage,
    showSuccessToast = true,
    showErrorToast = true
  } = options

  const [mutation, { data, isLoading, isError, error, refetch }] = mutationHook()
  const [hasError, setHasError] = useState(false)

  const submit = useCallback(
    async payload => {
      try {
        setHasError(false)
        const response = await mutation(payload).unwrap()

        if (showSuccessToast) {
          customToastWithAlert({
            message: successMessage,
            type: 'success'
          })
        }

        if (onSuccess) {
          onSuccess(response)
        }
        return response
      } catch (err) {
        setHasError(true)
        const errorMsg = errorMessage || err.data?.description || err.error || 'An error occurred during the operation'
        if (showErrorToast) {
          customToastWithAlert({
            message: errorMsg,
            type: 'error'
          })
        }
        if (onError) {
          onError(err)
        }
        throw err
      }
    },
    [mutation, onSuccess, onError, successMessage, errorMessage, showSuccessToast, showErrorToast]
  )

  return {
    submit,
    refetch,
    data,
    isLoading,
    isError: isError || hasError,
    error
  }
}

export default useMutation
